import React, { createContext, forwardRef, useContext, useState } from 'react';
import { Box, IconButton, TextField } from '@mui/material';
import type { TextFieldProps } from '@mui/material';
import type { SxProps, Theme } from '@mui/material/styles';
import RestoreIcon from '@mui/icons-material/Restore';

// This client-rendered form also supports React typings predating useId.
let reviewIdSequence = 0;

function useReviewId() {
  const [id] = useState(() => `bold-field-review-${++reviewIdSequence}`);
  return id;
}

const FieldReviewContext = createContext<FieldReviewState>({ changes: {}, locked: false, undo: () => { } });

export const FieldReviewProvider = FieldReviewContext.Provider;

export function useFieldReview(name?: string) {
  const context = useContext(FieldReviewContext);
  return {
    change: name ? context.changes[name] : undefined, locked: context.locked,
    restore: () => { if (name && !context.locked && context.changes[name]?.canUndo) context.undo([name]); }
  };
}

export function FieldRestoreButton({ name, label }: { name: string; label: React.ReactNode }) {
  const { change, locked, restore } = useFieldReview(name);
  if (!change) return null;
  const description = change.editedAfter ? 'Edited after assistant; current value is protected'
    : 'Restore previous value';
  return <IconButton type="button" size="small" className="field-review-restore" aria-label={`Restore previous ${typeof label === 'string' ? label : name}`} title={description} disabled={locked || !change.canUndo} onMouseDown={event => { event.preventDefault(); event.stopPropagation(); }} onKeyDown={event => event.stopPropagation()} onClick={event => { event.preventDefault(); event.stopPropagation(); restore(); }} sx={{ color: 'info.light', padding: '4px', opacity: 0, transition: 'opacity 120ms ease', '&:focus-visible': { opacity: 1, outline: '2px solid currentColor', outlineOffset: 1 }, '@media (hover: none)': { opacity: 1 } }}>
    <RestoreIcon fontSize="small" />
  </IconButton>;
}

export function mergeFieldSx(...styles: (SxProps<Theme> | undefined)[]): SxProps<Theme> {
  return styles.flatMap(style => style == null ? [] : Array.isArray(style) ? style : [style]) as SxProps<Theme>;
}

// Keep the concrete element return for older React ComponentType declarations.
// The call signature also avoids propTypes variance across MUI's variant union.
export const ReviewTextField = forwardRef<HTMLDivElement, ReviewTextFieldProps>(function ReviewTextField(props, ref) {
  const { name, label, InputProps, InputLabelProps, inputProps, sx, disabled, ...rest } = props;
  const { change, locked } = useFieldReview(name);
  const descriptionId = useReviewId();
  const generatedId = useReviewId();
  const fieldId = props.id ?? generatedId;
  const describedBy = [inputProps?.['aria-describedby'], props.helperText ? `${fieldId}-helper-text` : undefined,
  change ? descriptionId : undefined].filter(Boolean).join(' ') || undefined;
  const hasTrailingControl = Boolean(props.select || InputProps?.endAdornment || props.type === 'number');
  const reviewSx: SxProps<Theme> = change ? {
    '& .MuiOutlinedInput-root': {
      position: 'relative',
      '&:not(.Mui-error) .MuiOutlinedInput-notchedOutline': { borderColor: '#86bde8' },
      '&:hover .field-review-restore, &:focus-within .field-review-restore': { opacity: 1 },
      '& > .MuiInputBase-input': { paddingTop: '24px', paddingBottom: '9px', paddingRight: hasTrailingControl ? '76px' : '42px' },
      '&.MuiInputBase-multiline': { paddingTop: '24px', paddingBottom: '9px', paddingRight: '42px' },
      '&.MuiInputBase-multiline > textarea.MuiInputBase-input': { padding: 0 },
    },
  } : {};
  return <TextField {...rest} ref={ref} id={fieldId} name={name} label={label} disabled={disabled || locked} inputProps={{ ...inputProps, 'aria-describedby': describedBy }} InputLabelProps={change ? { ...InputLabelProps, shrink: true } : InputLabelProps} InputProps={change ? {
    ...InputProps,
    startAdornment: <>{InputProps?.startAdornment}<Box component="span" id={descriptionId} title={`Previous: ${change.beforeLabel}${change.editedAfter ? ' (edited after assistant; undo protected)' : ''}`} sx={{ position: 'absolute', top: 6, left: 14, right: hasTrailingControl ? 76 : 44, fontStyle: 'italic', fontSize: '.68rem', lineHeight: 1, opacity: .8, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', pointerEvents: 'none' }}>
      <Box component="span" sx={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clipPath: 'inset(50%)' }}>Previous: </Box>
      {change.beforeLabel}{change.editedAfter ? ' · edited; undo protected' : ''}
    </Box></>,
    endAdornment: <><Box component="span" sx={{ position: 'absolute', right: hasTrailingControl ? 36 : 4, top: '50%', transform: 'translateY(-50%)', zIndex: 1 }}>
      <FieldRestoreButton name={name!} label={label} />
    </Box>{InputProps?.endAdornment}</>,
  } : InputProps} sx={mergeFieldSx(sx, reviewSx)} />;
}) as (props: TextFieldProps & React.RefAttributes<HTMLDivElement>) => React.ReactElement | null;

export function CheckboxReview({ name, label, children }: { name: string; label: React.ReactNode; children: React.ReactNode }) {
  const { change } = useFieldReview(name);
  const descriptionId = useReviewId();
  return <Box role={change ? 'group' : undefined} aria-label={change ? String(label ?? name) : undefined} aria-describedby={change ? descriptionId : undefined} sx={{ display: 'flex', alignItems: 'center', position: 'relative', minWidth: 0, ...(change ? { borderRadius: 1, boxShadow: 'inset 0 0 0 1px #86bde8', width: '100%', '& .MuiFormControlLabel-root': { marginLeft: 0, marginRight: 0, maxWidth: '100%' }, '& .MuiCheckbox-root': { padding: '6px' }, '& .MuiFormControlLabel-label': { minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' } } : {}), '&:hover .field-review-restore, &:focus-within .field-review-restore': { opacity: 1 } }}>
    <Box sx={{ minWidth: 0 }}>{change && <Box id={descriptionId} title={`Previous: ${change.beforeLabel}`} sx={{ fontSize: '.68rem', fontStyle: 'italic', opacity: .8, lineHeight: 1.2, paddingLeft: '9px', paddingRight: '28px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '100%' }}>
      Previous: {change.beforeLabel}{change.editedAfter ? ' · undo protected' : ''}
    </Box>}{children}</Box>
    {change && <Box sx={{ position: 'absolute', right: 2, top: -4 }}><FieldRestoreButton name={name} label={label} /></Box>}
  </Box>;
}

// Presentation only. The host owns the baseline, conflict checks and undo operation.
export type FieldChange = { beforeLabel: string; editedAfter: boolean; canUndo: boolean };

export type FieldReviewState = {
  changes: Record<string, FieldChange>;
  locked: boolean;
  undo: (fields: string[]) => void;
};

// A stable TextField slot also used by DateTimePicker. Preserve its refs, input props,
// handlers and calendar adornment; never replace the picker control or its state.
type ReviewTextFieldProps = Omit<TextFieldProps, 'variant'> & { variant?: TextFieldProps['variant'] };
